/**
 * Landing Page
 *
 * Public page shown at the root URL.
 * Displays hero section, platform stats, and featured token placeholders.
 * All content is visible without wallet connection (FR1).
 * CTA buttons that require auth trigger the connect prompt.
 */

import { Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";

import { Button } from "../components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/Card";
import { DataFreshness } from "../components/ui/DataFreshness";
import { Spinner } from "../components/ui/Spinner";
import { usePlatformStats } from "../hooks/api/use-platform-stats";
import { useRequireAuth } from "../hooks/use-require-auth";
import { fadeInUp, scaleIn, staggerContainer, staggerItem } from "../lib/animations";
import { cn } from "../lib/cn";

function formatVolume(volume: string): string {
	const num = Number(volume);
	if (Number.isNaN(num)) return "$0";
	if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
	if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
	return `$${num.toFixed(2)}`;
}

function formatNumber(value: number): string {
	return value.toLocaleString();
}

type StatCardData = {
	title: string;
	value: string;
	description: string;
};

function StatCard({ title, value, description }: StatCardData) {
	return (
		<motion.div variants={staggerItem}>
			<Card className="text-center">
				<CardHeader>
					<CardDescription>{title}</CardDescription>
				</CardHeader>
				<CardContent>
					<p className="text-3xl font-bold text-pastel-vibrant-blue">{value}</p>
					<p className="text-sm text-pastel-gray mt-1">{description}</p>
				</CardContent>
			</Card>
		</motion.div>
	);
}

function StatCardLoading() {
	return (
		<motion.div variants={staggerItem}>
			<Card className="text-center">
				<CardHeader>
					<div className="skeleton h-4 w-24 mx-auto" />
				</CardHeader>
				<CardContent className="flex flex-col items-center gap-2">
					<Spinner size="md" />
					<div className="skeleton h-3 w-20" />
				</CardContent>
			</Card>
		</motion.div>
	);
}

function HeroSection() {
	const { isAuthenticated, promptConnect } = useRequireAuth();
	const navigate = useNavigate();

	function handleCreateToken() {
		if (isAuthenticated) {
			navigate({ to: "/launch" });
		} else {
			promptConnect();
		}
	}

	return (
		<motion.section
			variants={fadeInUp}
			initial="hidden"
			animate="visible"
			className="relative overflow-hidden rounded-2xl px-6 py-16 md:py-24 text-center"
			style={{ background: "var(--gradient-pastel-hero)" }}
		>
			<div className="relative z-10 max-w-3xl mx-auto">
				<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-pastel-black mb-4">
					<span className="text-gradient">One-click token launch</span> with instant AMM liquidity
				</h1>
				<p className="text-lg md:text-xl text-pastel-gray max-w-2xl mx-auto mb-8">
					Deploy your token with a Uniswap V4 pool in a single transaction. No bonding curves. No
					graduation. Instant trading from block one.
				</p>

				<motion.div
					variants={scaleIn}
					initial="hidden"
					animate="visible"
					className="flex flex-col sm:flex-row items-center justify-center gap-4"
				>
					<Button variant="gradient" size="lg" onClick={handleCreateToken}>
						Create Token
					</Button>
					<Link to="/discover">
						<Button variant="outline" size="lg">
							Discover Tokens
						</Button>
					</Link>
				</motion.div>
			</div>
		</motion.section>
	);
}

function StatsSection() {
	const { data, isLoading, isError } = usePlatformStats();

	const stats: StatCardData[] = data
		? [
				{
					title: "Tokens Launched",
					value: formatNumber(data.tokensLaunched),
					description: "deployed on the platform",
				},
				{
					title: "Trading Volume",
					value: formatVolume(data.totalVolume),
					description: "total trading volume",
				},
				{
					title: "Unique Creators",
					value: formatNumber(data.activeWallets),
					description: "token deployers",
				},
			]
		: [];

	return (
		<section className="py-12">
			<h2 className="text-2xl font-bold text-pastel-black text-center mb-8">Platform Stats</h2>

			{isError ? (
				<p className="text-center text-pastel-gray">
					Unable to load platform stats. Please try again later.
				</p>
			) : (
				<motion.div
					variants={staggerContainer}
					initial="hidden"
					animate="visible"
					className="grid grid-cols-1 sm:grid-cols-3 gap-6"
				>
					{isLoading ? (
						<>
							<StatCardLoading />
							<StatCardLoading />
							<StatCardLoading />
						</>
					) : (
						stats.map((stat) => <StatCard key={stat.title} {...stat} />)
					)}
				</motion.div>
			)}

			{data && (
				<div className="flex justify-center mt-4">
					<DataFreshness
						source={data.dataSource}
						updatedAt={new Date(Date.now() - data.dataAge * 1000)}
					/>
				</div>
			)}
		</section>
	);
}

function FeaturedTokensSection() {
	const placeholders = [
		"Trending tokens arriving soon",
		"Discover top performers",
		"Community favorites",
	];

	return (
		<section className="py-12">
			<h2 className="text-2xl font-bold text-pastel-black text-center mb-8">Featured Tokens</h2>
			<div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
				{placeholders.map((text) => (
					<Card key={text} className={cn("text-center opacity-60")}>
						<CardHeader>
							<CardTitle>Coming Soon</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-pastel-gray">{text}</p>
						</CardContent>
					</Card>
				))}
			</div>
		</section>
	);
}

export function LandingPage() {
	return (
		<div className="space-y-8">
			<HeroSection />
			<StatsSection />
			<FeaturedTokensSection />
		</div>
	);
}
